// Adds the photos and videos of a public post on X to the folder on screen.
//
// A link is read as soon as a whole one is pasted, or when the form is sent with whatever is in
// the field, so the API can say why it isn't one. The post comes back as its author, its text and
// a tile for each photo or video, every one picked to begin with. Each way a post can't be had is
// told in the API's own words.
//
// Adding fetches the picked media from X one after another, here in the browser, and hands the
// files over together once all of them have arrived (see "xPostTools"); from there they are uploads
// like any other. A file X won't hand over stops the adding and says which, leaving the rest to try
// again. Closing the dialog while it fetches drops what has arrived.
import classnames from 'classnames'
import PropTypes from 'prop-types'
import {useApolloClient} from '@apollo/client/react'
import {useEffect, useRef, useState} from 'react'

import CheckIcon from '@mui/icons-material/CheckRounded'
import Dialog from '@mui/material/Dialog'
import LinkIcon from '@mui/icons-material/LinkRounded'

import {BaseButton} from '../shared/blocks/Buttons'
import {Text} from '../shared/blocks/Texts'

import {X_POST} from '../../graphql/queries/xPosts'
import {NOT_A_POST, POST_HIDDEN, POST_NOT_FOUND, POST_WITHOUT_MEDIA, PROVIDER_BUSY, says} from '../../lib/errorTools'
import {fetchMedia, isPostLink, postText, postedOn, runtime} from '../../lib/xPostTools'

import './importDialog.scss'

const TOLD_AS_SAID = [NOT_A_POST, POST_NOT_FOUND, POST_HIDDEN, POST_WITHOUT_MEDIA]

const refusalOf = error => {
  if (says(error, PROVIDER_BUSY)) return 'X is busy. Try again in a few minutes.'

  return TOLD_AS_SAID.find(sentence => says(error, sentence)) ?? 'X couldn’t be asked for this post. Try again.'
}

const tileLabel = ({kind, width, height, durationMs}) => {
  if (kind === 'gif') return 'GIF'
  if (kind === 'video') return durationMs ? runtime(durationMs) : 'Video'

  return width && height ? `${width} × ${height}` : null
}

const MediaTile = ({media, picked, onToggle}) => {
  const label = tileLabel(media)

  return (
    <button
      type='button'
      className={classnames('x-media', {'left-out': !picked})}
      aria-pressed={picked}
      aria-label={`${picked ? 'Leave out' : 'Include'} ${media.filename}`}
      onClick={onToggle}>
      {media.previewUrl && <img src={media.previewUrl} alt='' loading='lazy' />}
      <span className={classnames('x-media-check', {picked})}>{picked && <CheckIcon />}</span>
      {label && <span className='x-media-label'>{label}</span>}
    </button>
  )
}

const MEDIA_SHAPE = PropTypes.shape({
  kind: PropTypes.oneOf(['photo', 'video', 'gif']).isRequired,
  url: PropTypes.string.isRequired,
  previewUrl: PropTypes.string,
  filename: PropTypes.string.isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
  durationMs: PropTypes.number
})

MediaTile.propTypes = {
  media: MEDIA_SHAPE.isRequired,
  picked: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired
}

const XPostDialog = ({destination, onClose, onAdd}) => {
  const client = useApolloClient()
  const [link, setLink] = useState('')
  const [reading, setReading] = useState(false)
  const [post, setPost] = useState(null)
  const [picked, setPicked] = useState([])
  const [problem, setProblem] = useState(null)
  // How many of the picked media have arrived, while they are being fetched.
  const [fetched, setFetched] = useState(null)
  // Each read is numbered, so a slower answer to a link since replaced is let go.
  const lastRead = useRef(0)
  const closed = useRef(false)

  // Set again on every mount, since StrictMode unmounts and remounts once in development.
  useEffect(() => {
    closed.current = false

    return () => {
      closed.current = true
    }
  }, [])

  const read = value => {
    const reads = ++lastRead.current

    setReading(true)
    setPost(null)
    setProblem(null)

    client.query({query: X_POST, variables: {link: value.trim()}, fetchPolicy: 'no-cache'})
      .then(({data}) => {
        if (reads !== lastRead.current) return

        setPost(data.xPost)
        setPicked(data.xPost.media.map(({filename}) => filename))
      })
      .catch(error => {
        if (reads === lastRead.current) setProblem(refusalOf(error))
      })
      .finally(() => {
        if (reads === lastRead.current) setReading(false)
      })
  }

  const change = value => {
    setLink(value)

    if (isPostLink(value)) return read(value)

    lastRead.current += 1
    setReading(false)
    setPost(null)
    setProblem(null)
  }

  const toggle = filename => setPicked(current => (
    current.includes(filename) ? current.filter(name => name !== filename) : [...current, filename]
  ))

  const chosen = post ? post.media.filter(({filename}) => picked.includes(filename)) : []
  const adding = fetched !== null

  const add = async () => {
    const files = []

    setProblem(null)

    for (const media of chosen) {
      setFetched(files.length)

      try {
        files.push(await fetchMedia(media))
      } catch {
        if (closed.current) return

        setFetched(null)
        setProblem(`X wouldn’t hand over “${media.filename}”. Try again.`)
        return
      }

      if (closed.current) return
    }

    onAdd(files)
  }

  const handleSubmit = event => {
    event.preventDefault()

    if (post) add()
    else read(link)
  }

  const confirmLabel = () => {
    if (adding) return `Fetching ${fetched + 1} of ${chosen.length}…`
    if (!post) return 'Add'

    return `Add ${chosen.length} file${chosen.length === 1 ? '' : 's'}`
  }

  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby='x-post-title'
      slotProps={{paper: {className: 'import-dialog x-post-dialog', component: 'form', onSubmit: handleSubmit}}}>
      <div className='dialog-head'>
        <LinkIcon />
        <h2 className='dialog-title' id='x-post-title'>Import from X</h2>
      </div>
      <Text className='dialog-body'>
        Photos and videos from a public post are added to “{destination}”. The post stays on X.
      </Text>

      <label className='dialog-field'>
        <span className='field-label'>Link to a post</span>
        <input
          type='url'
          value={link}
          placeholder='x.com/name/status/…'
          autoComplete='off'
          spellCheck='false'
          autoFocus
          disabled={adding}
          onChange={event => change(event.target.value)}
        />
      </label>

      {reading && <Text className='dialog-note' role='status'>Reading the post…</Text>}
      {problem && <Text className='dialog-error' role='alert'>{problem}</Text>}

      {post && (
        <div className='x-post'>
          <div className='x-post-head'>
            <span className='x-post-author'>{post.authorName}</span>
            <span className='x-post-handle'>@{post.handle} · {postedOn(post.postedAt)}</span>
          </div>
          {postText(post.text) && <p className='x-post-text'>{postText(post.text)}</p>}
          <div className='x-post-media'>
            {post.media.map(media => (
              <MediaTile
                key={media.filename}
                media={media}
                picked={picked.includes(media.filename)}
                onToggle={() => !adding && toggle(media.filename)}
              />
            ))}
          </div>
        </div>
      )}

      <div className='dialog-actions'>
        <BaseButton className='dialog-cancel' variant='text' onClick={onClose}>Cancel</BaseButton>
        <BaseButton
          className='dialog-confirm'
          variant='text'
          type='submit'
          disabled={adding || reading || (post ? !chosen.length : !link.trim())}>
          {confirmLabel()}
        </BaseButton>
      </div>
    </Dialog>
  )
}

XPostDialog.propTypes = {
  // The folder the files will land in, by name.
  destination: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  // Called with the fetched media as Files, all of them at once.
  onAdd: PropTypes.func.isRequired
}

export default XPostDialog
